import { graphql } from "~/graphql/codegen";
import { apolloLoader, getServerClient } from "./graphql";
import { canUseMeetupArrivalOnboarding } from "./utils/meetup-onboarding.server";
import type {
  ContextAuthenticatedQuery,
  ContextAuthenticatedQueryVariables,
  ContextNotAuthenticatedQuery,
  ContextNotAuthenticatedQueryVariables,
} from "~/graphql/codegen/graphql";
import {
  UserBillingStatus,
  ContextAuthenticatedDocument,
  ContextNotAuthenticatedDocument,
} from "~/graphql/codegen/graphql";
import { ConfigContext, WebConfig } from "./hooks/webconfig";
import {
  GoogleTagManagerBody,
  GoogleTagManagerHead,
} from "./components/google-tag-manager";
import styles from "./tailwind.css?url";

const SetUserPreferencesDocument = graphql(`
  mutation SetUserPreferences($preferences: SetPreferencesInput!) {
    setPreferences(preferences: $preferences) {
      theme
    }
  }
`);

const CONTEXT_NOT_AUTHENTICATED_DOCUMENT = graphql(`
  fragment Config on Config {
    siteURL
    api
    appUrl
    communityUrl
    rudderstack {
      url
      writeKey
    }
    oauth {
      apple
      google
      linkedIn
    }
    hubspotPortalId
    environment
    growthbookKey
    googleMapKey
    liveAvatarCoachEnabled
  }

  fragment SpaceMetadata on Mindspace {
    id
    uniqueName
    name
    icon
    partner {
      name
      logo
      brandColor
      primaryColor
      secondaryColor
      showMindstoneLogo
      website
    }
    images {
      thumbnail
    }
    configuration {
      public
      membersCanAddResources
      browserExtensionDisabled
    }
    modules {
      resources
      objectives
      events
      programs
      sandbox
      sandboxHistory
      aiDisclaimer
      aiDisclaimerText
      aiDisclaimerDetail
      # Per-Mindspace capability settings. Non-null Booleans that default to
      # true server-side, so selecting them here cannot introduce a state where
      # a Live learner loses the coach or support because a field was absent.
      liveAvatarCoachEnabled
      customerSupportEnabled
    }
    currentUserConfiguration {
      notificationsMuted
    }
    currentUserPermission {
      role
      isChampion
      isCoach
    }
    programs {
      enrolled {
        total
        items {
          id
          metadata {
            slug
          }
          cohorts {
            id
            name
            enrolledAt
            metadata {
              startDate
              endDate
            }
          }
        }
      }
    }
  }

  query ContextNotAuthenticated($identifier: String!) {
    config {
      ...Config
    }
    mindspace(identifier: $identifier) {
      ...SpaceMetadata
      # Deliberately selected HERE and not inside the SpaceMetadata fragment:
      # routes/_app spreads that same fragment over mindspaces.all (the space
      # switcher), so a field on the fragment becomes one existence probe per
      # Mindspace the user belongs to, on every page load. The nav only needs the
      # answer for the CURRENT space, so it is threaded separately -- see
      # hasPublishedEvents in the loader payload and in MainContext.
      #
      # Mindspace.events resolves to null whenever the events module is off, which
      # is exactly the short-circuit we want: no probe runs for a space whose tab
      # is disabled anyway.
      events {
        hasPublishedEvents
      }
    }
    defaultPlatformSpace {
      id
    }
  }
`);

const CONTEXT_AUTHENTICATED_DOCUMENT = graphql(`
  fragment CurrentUser on CurrentUser {
    id
    email
    isStaff
    selfServePolicy
    language
    preferences {
      theme
      orderBy
      state {
        orderBy
      }
    }
    referrals {
      referralCode
    }
    billing {
      status
      purchaseDate
      renewalDate
      billingUser {
        id
      }
    }
  }

  fragment MainProfile on Profile {
    __typename
    id
    email
    hasAddedResources
    name: displayName
    displayName
    username
    profilePictureURL
    tagLine
    following
    company
    position
    bio
    roleContextShort
    mine
    joinedDate
    level {
      level
    }
    evidencing {
      points
    }
  }

  query ContextAuthenticated($identifier: String!) {
    mindspace(identifier: $identifier) {
      programs {
        enrolled {
          total
        }
      }
    }
    mindspaces {
      all {
        programs {
          enrolled {
            total
          }
        }
      }
    }
    myProfile {
      ...MainProfile
    }
    currentUser {
      ...CurrentUser
    }
    getUserTags {
      tags {
        name
      }
    }
    defaultPlatformSpace {
      id
    }
  }
`);

import { data, LoaderFunctionArgs, redirect } from "react-router";
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
} from "react-router";
import React, { useEffect, useMemo, useState } from "react";
import HubSpotInitializer from "~/components/hubspot-initializer";
import { LiveAvatarCoachMount } from "~/components/live-avatar-coach-mount";
import { GenericBoundary } from "~/components/error-boundaries/error-boundaries";
import { rootGetAccessToken } from "~/utils/ensure-authenticated.server";
import { parseCookieHeader } from "../../shared/cookie-header";

import {
  accessTokenCookie,
  attributionCookie,
  csrfProtectionCookie,
  getAttributionTrackingData,
  getRefFromUrl,
  loggedInCookie,
  referralRefCookie,
  refreshTokenCookie,
} from "~/utils/oauth.server";
import { MainContext } from "~/hooks/context-hook";
import {
  AuthCheckProvider,
  currentSpaceUniqueName,
} from "~/hooks/check-authenticated";
import {
  extractGraphQLErrorMessage,
  resolveNoSpaceAccessRedirect,
  resolveUnknownSpaceRedirect,
} from "~/utils/space-routing";

import { Toaster } from "@mindstonehq/ui";
import * as Sentry from "@sentry/react";
import metrics from "./utils/metrics";
import { I18nProvider } from "@lingui/react";
import { activateLocale, i18n } from "./i18n/i18n";
import { ApolloHydrationHelper } from "@apollo/client-integration-react-router";
import { TooltipProvider } from "@mindstonehq/ui";
import ProfileInitializer from "./components/profile-initializer";
import { FirstPointsProvider } from "./components/points/first-points-context";
import { AIUsageProvider } from "./components/ai-usage/ai-usage-context";
import { GlobalAIUsageReminderPopover } from "./components/ai-usage/global-ai-usage-reminder-popover";
import { GlobalPointsEarnedPopover } from "./components/points/global-first-points-earned-popover";
import { ApiConnectivityNotice } from "~/components/containers/api-connectivity-notice";
import { MindstonePlusUpsellProvider } from "./components/upsell/mindstone-plus-upsell-context";
import {
  FeatureDefinition,
  GrowthBook,
  GrowthBookProvider,
} from "@growthbook/growthbook-react";
import { useMutation } from "@apollo/client/react";

export function links() {
  return [
    { rel: "icon", type: "image/png", href: "/favicon.ico" },
    {
      rel: "icon",
      type: "image/png",
      sizes: "32x32",
      href: "/favicon-32x32.png",
    },
    {
      rel: "icon",
      type: "image/png",
      sizes: "16x16",
      href: "/favicon-16x16.png",
    },
    { rel: "stylesheet", href: styles },
    { rel: "stylesheet", href: "https://rsms.me/inter/inter.css" },
    {
      rel: "stylesheet",
      href: "https://fonts.googleapis.com/css2?family=Figtree:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,300;1,400;1,500;1,600;1,700;1,800&display=swap",
    },
  ];
}

export async function loader({ request }: LoaderFunctionArgs) {
  const url = new URL(request.url);
  const accessTokenResult = await rootGetAccessToken(request);
  let accessToken = "";
  let isNewAccessToken = false;
  if (accessTokenResult) {
    accessToken = accessTokenResult.accessToken;
    isNewAccessToken = accessTokenResult.new;
  }
  const uniqueName = currentSpaceUniqueName(request);

  let context = {};
  if (accessToken) {
    context = {
      headers: {
        Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
      },
    };
  }

  let configResult: ContextNotAuthenticatedQuery | undefined;
  let contextResult: ContextAuthenticatedQuery | undefined;

  try {
    const { data, error } = await getServerClient().query({
      query: ContextNotAuthenticatedDocument,
      fetchPolicy: "no-cache",
      variables: {
        identifier: uniqueName,
      },
      context,
    });
    if (error) {
      console.error("error getting non authenticated context", error);
      // FOX-3618: preserve the real GraphQL error (code + message) instead of
      // collapsing it to a bare string. The catch keys the loop-breaker on
      // auth state, but `resolveUnknownSpaceRedirect` + observability still need
      // the real terror code (e.g. not_found.mindspace / forbidden.mindspace).
      throw error;
    }
    configResult = data;

    if (accessToken) {
      const { data, error } = await getServerClient().query({
        query: ContextAuthenticatedDocument,
        fetchPolicy: "no-cache",
        variables: {
          identifier: uniqueName,
        },
        context,
      });
      if (error || !data) {
        console.error("error getting authenticated context", error);
        // FOX-3618: preserve the real GraphQL error so the catch can classify
        // it (see the non-authenticated branch above).
        throw error ?? new Error("empty authenticated context");
      }
      contextResult = data;
    }
  } catch (e: any) {
    const errorMessage = extractGraphQLErrorMessage(e);
    console.error("catch: error getting context", { errorMessage, error: e });
    const unknownSpaceRedirect = resolveUnknownSpaceRedirect({
      url,
      uniqueName,
      errorMessage,
    });
    if (unknownSpaceRedirect) return redirect(unknownSpaceRedirect, 302);
    if (url.pathname.includes("/events") && url.pathname.includes("/join")) {
      if (accessToken && errorMessage.includes("forbidden.mindspace")) {
        // FOX-3618: an authenticated non-member hitting a forbidden event-join
        // must land on the terminal `/no-space-access` page (which exists) — NOT
        // the non-existent `/event-no-access` route, which 404s into the root
        // error boundary. Reuse the same helper as the general no-access bounce
        // below so both paths share one terminal page and host/loop-safe logic.
        const eventNoAccessRedirect = resolveNoSpaceAccessRedirect({
          url,
          uniqueName,
          authenticated: !!accessToken,
        });
        if (eventNoAccessRedirect) return redirect(eventNoAccessRedirect, 302);
      }

      const redirectUrl = url.toString().replace("http://", "https://");
      return redirect(
        `/signup?onlineEventRedirect=true&eventFlow=program&redirectUrl=${encodeURIComponent(redirectUrl)}`,
        302,
      );
    }
    if (url.pathname.includes("/events") && url.pathname.endsWith("/live")) {
      const redirectUrl = url.toString().replace("http://", "https://");
      return redirect(
        `/signup?onlineEventRedirect=true&eventFlow=community&redirectUrl=${encodeURIComponent(redirectUrl)}`,
        302,
      );
    }
    // FOX-3618: an authenticated user whose space access failed must NEVER be
    // sent to same-host `/login` — identity reflects the same valid session
    // straight back, producing an infinite redirect loop. Bounce them to a
    // terminal "no access to this space yet" page on the app host instead. Only
    // a genuinely unauthenticated request falls through to `/login` below.
    const noSpaceAccessRedirect = resolveNoSpaceAccessRedirect({
      url,
      uniqueName,
      authenticated: !!accessToken,
    });
    if (noSpaceAccessRedirect) return redirect(noSpaceAccessRedirect, 302);
    if (url.pathname.includes("/calendar-export")) {
      const redirectUrl = url.toString().replace("http://", "https://");
      return redirect(
        `/login?redirectUrl=${encodeURIComponent(redirectUrl)}`,
        302,
      );
    }
    return redirect(
      `/login?redirectUrl=${encodeURIComponent(url.toString())}`,
      302,
    );
  }

  if (!configResult) {
    throw new Error("Failed to fetch configuration");
  }

  const config = configResult.config;
  const FEATURES_ENDPOINT =
    "https://cdn.growthbook.io/api/features/" + config.growthbookKey;
  const featuresDataPromise = fetch(FEATURES_ENDPOINT);

  let features = {} as Record<string, FeatureDefinition>;
  try {
    const featuresDataRes = await featuresDataPromise;
    const featuresData = await featuresDataRes.json();
    features = featuresData.features;
  } catch (error) {
    console.error("error getting feature flags", error);
  }

  // Attribution tracking
  const attributionData = getAttributionTrackingData(request.url);
  const headers = new Headers();
  if (attributionData) {
    headers.append(
      "Set-Cookie",
      await attributionCookie.serialize(attributionData),
    );
  }
  const referralCode = getRefFromUrl(request.url);
  if (referralCode) {
    headers.append("Set-Cookie", await referralRefCookie.serialize(referralCode));
  }
  if (isNewAccessToken) {
    headers.append("Set-Cookie", accessTokenCookie(accessToken));
    headers.append("Set-Cookie", loggedInCookie());
    //TODO: IN a few months, remove the refresh token stuff below. This was to migrate cookies to lax mode
    const cookieHeader = request.headers.get("Cookie");
    if (cookieHeader) {
      const parsedCookie = parseCookieHeader(cookieHeader);
      if (parsedCookie["x-ms-refresh-token"]) {
        headers.append(
          "Set-Cookie",
          refreshTokenCookie(parsedCookie["x-ms-refresh-token"]),
        );
      }
    }
  }
  headers.append("Set-Cookie", csrfProtectionCookie(request));

  if (!accessToken) {
    // /events/<slug>/live is reached primarily by scanning the on-screen QR
    // code at a community event. Treat it like /events/<slug>/join: send the
    // user through /signup (not /login) by default so first-time attendees
    // hit the create-account flow, and preserve redirectUrl so identity
    // bounces them straight back to /live where the loader auto-checks them
    // in. Identity still exposes "Already have an account? Sign in" for
    // returning users.
    if (url.pathname.includes("/events") && url.pathname.endsWith("/live")) {
      const redirectUrl = url.toString().replace("http://", "https://");
      return redirect(
        `/signup?onlineEventRedirect=true&eventFlow=community&redirectUrl=${encodeURIComponent(redirectUrl)}`,
        302,
      );
    }
    // User is not authenticated. If a public Mindspace, we allow to go through, otherwise
    // we redirect to login
    if (!configResult.mindspace.configuration?.public) {
      return redirect(
        `/login?redirectUrl=${encodeURIComponent(url.toString())}`,
        302,
      );
    }
    // TODO: Move join URL to faciliate it being unauthenticated
    // TODO: Investigate why this is http here
    if (url.pathname.includes("/events") && url.pathname.includes("/join")) {
      const redirectUrl = url.toString().replace("http://", "https://");
      return redirect(
        `/signup?onlineEventRedirect=true&eventFlow=program&redirectUrl=${encodeURIComponent(redirectUrl)}`,
        302,
      );
    }

    // We're good and we can stay here. Some pages will be behind signup wall.
  } else {
    // Check if user has any enrolled programs across all their mindspaces
    const userHasEnrolledPrograms =
      contextResult?.mindspaces?.all?.some(
        (mindspace) => (mindspace?.programs?.enrolled?.total || 0) > 0,
      ) ?? false;

    // Check if current URL should bypass onboarding
    const meetupOnboarding = !contextResult?.myProfile?.username &&
      await canUseMeetupArrivalOnboarding(request, uniqueName, String(accessToken));
    const shouldBypassOnboarding =
      meetupOnboarding ||
      url.pathname.includes("/enroll/") || // always allow enrollment flow
      url.pathname.includes("/seats/") || // always allow seat assignment
      (url.pathname.includes("/programs/") && !userHasEnrolledPrograms); // allow programs page if user has no enrollments

    // Form/fetcher submissions hit the loader after the action runs. Returning
    // a redirect here clobbers the action's response (the request becomes the
    // 302 + onboarding HTML), so any API consumer of an action endpoint sees
    // a misleading 200/302 instead of either the action result or a clean
    // precondition_failed. For non-GET requests we let the action's response
    // through unchanged — the user can still be redirected to /onboarding on
    // their next GET navigation.
    if (
      request.method === "GET" &&
      !contextResult?.myProfile?.username &&
      !shouldBypassOnboarding
    ) {
      const appUrl = url.hostname.replace(uniqueName, "app");
      return redirect(
        `https://${appUrl}/onboarding?redirectUrl=${encodeURIComponent(url.toString())}`,
        302,
      );
    }
  }

  return data(
    {
      config: {
        API: config.api,
        graphqlAPI: process.env.GRAPHQL_API,
        appUrl: `https://${url.hostname}`,
        communityUrl: config.communityUrl,
        communityAdminUrl:
          process.env.COMMUNITY_ADMIN_URL?.replace(/\/$/, "") ??
          config.communityUrl.replace(/\/$/, ""),
        diagnosticUrl: process.env.DIAGNOSTIC_URL?.replace(/\/$/, "") ?? "",
        rudderstackURL: config.rudderstack?.url,
        rudderstackWriteKeyWebApp: config.rudderstack?.writeKey,
        revision: process.env.REVISION,
        oauthAppleRedirectUrl: config.oauth?.apple.replaceAll(
          "app.",
          "identity.",
        ),
        oauthGoogleRedirectUrl: config.oauth?.google.replaceAll(
          "app.",
          "identity.",
        ),
        oauthLinkedinRedirectUrl: config.oauth?.linkedIn.replaceAll(
          "app.",
          "identity.",
        ),
        hubspotPortalId: config.hubspotPortalId,
        environment: config.environment,
        growthbookKey: config.growthbookKey,
        googleApiKey: config.googleMapKey,
        liveAvatarCoachEnabled: config.liveAvatarCoachEnabled,
      } as WebConfig,
      preferences: contextResult?.currentUser?.preferences || { theme: "" },
      defaultPlatformSpace:
        contextResult?.defaultPlatformSpace ||
        configResult.defaultPlatformSpace,
      currentSpace: configResult.mindspace,
      // Threaded as a bare scalar rather than left on `currentSpace` so the nav
      // policy has one unambiguous input, and so nothing tempts a future author
      // into moving the selection onto the shared SpaceMetadata fragment (which
      // would fan the probe out across every Mindspace in the space switcher).
      // `events` is null when the Mindspace's events module is off — the nav gate
      // ANDs the module flag anyway, so false is the right collapse.
      hasPublishedEvents:
        configResult.mindspace.events?.hasPublishedEvents ?? false,
      mindspaces: contextResult?.mindspaces,
      // Marketing's GTM container — every Mindspace, prod only.
      // See components/google-tag-manager.tsx.
      googleTagManagerEnabled: config.environment === "production",
      user: contextResult?.currentUser,
      profile: contextResult?.myProfile,
      tags:
        (contextResult?.getUserTags?.tags
          .filter((x) => x)
          .map((x) => x?.name) as string[]) || [],
      features,
    },
    {
      headers,
    },
  );
}

function App() {
  const {
    config: webConfig,
    defaultPlatformSpace,
    currentSpace,
    hasPublishedEvents,
    mindspaces,
    googleTagManagerEnabled,
    user,
    profile,
    tags,
    preferences,
    features,
  } = useLoaderData<typeof loader>();
  const client = getServerClient();
  const [updateUserPreference] = useMutation(SetUserPreferencesDocument, {
    client: client,
  });
  const [theme, setTheme] = useState(() => {
    return preferences.theme || "light";
  });

  const growthbook = useMemo<GrowthBook>(() => {
    let attributes = {};
    if (!user) {
      attributes = {
        id: metrics.getUserID(),
        isStaff: false,
        platform: "web",
      };
    } else {
      attributes = {
        id: user.id,
        isStaff: user.isStaff,
        platform: "web",
      };
    }
    return new GrowthBook({
      trackingCallback: (experiment, result) => {
        metrics.experimentStart(
          experiment.key,
          result.variationId,
          result.value,
        );
      },
      features,
      attributes,
    });
  }, [user, features]);

  useEffect(() => {
    metrics.init(webConfig);
  }, [webConfig]);

  useEffect(() => {
    if (!preferences.theme) {
      setTheme(
        window.matchMedia &&
          window.matchMedia("(prefers-color-scheme: dark)").matches
          ? "dark"
          : "light",
      );
    }

    if (user) {
      Sentry.getCurrentScope().setUser({ id: user.id });
      if (user.email) {
        metrics.addEmail(
          user.id,
          user.email,
          !!mindspaces?.all?.some(
            (mindspace) => mindspace?.programs?.enrolled?.total,
          ),
          user.billing?.status === UserBillingStatus.Active,
        );
      }
    }
    try {
      window.addEventListener("appinstalled", () => {
        metrics.track("pwa_installed");
      });
    } catch (error) {}
  }, [user, preferences]);

  const lang = activateLocale(user?.language);

  // Enrolled in at least one program cohort across all mindspaces — gates the
  // AI coach surfaces (live avatar launcher, community AskCoach). Same signal
  // the loader uses for the onboarding redirect.
  const hasEnrolledPrograms =
    mindspaces?.all?.some(
      (mindspace) => (mindspace?.programs?.enrolled?.total || 0) > 0,
    ) ?? false;

  return (
    <html lang={lang} className={theme}>
      <head>
        {googleTagManagerEnabled && <GoogleTagManagerHead />}
        <Meta />
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        <Links />
        <OurScripts webConfig={webConfig} />
      </head>
      <body className="min-h-[100dvh] bg-background text-foreground">
        {googleTagManagerEnabled && <GoogleTagManagerBody />}
        <I18nProvider i18n={i18n}>
        <GrowthBookProvider growthbook={growthbook}>
          <MainContext.Provider
            value={{
              isDefaultPlatformSpace:
                defaultPlatformSpace.id === currentSpace.id,
              currentSpace,
              hasPublishedEvents,
              authenticated: !!user,
              user,
              profile,
              tags,
              hasEnrolledPrograms,
              updateTheme: (theme: "" | "light" | "dark") => {
                updateUserPreference({
                  variables: {
                    preferences: {
                      theme,
                    },
                  },
                });
                if (theme === "") {
                  //system them
                  setTheme(
                    window.matchMedia &&
                      window.matchMedia("(prefers-color-scheme: dark)").matches
                      ? "dark"
                      : "light",
                  );
                  return;
                }
                setTheme(theme);
              },
            }}
          >
            <ConfigContext.Provider value={webConfig}>
              <ApolloHydrationHelper>
                <AuthCheckProvider>
                  <HubSpotInitializer>
                    <ProfileInitializer>
                      <TooltipProvider>
                        <FirstPointsProvider>
                          <AIUsageProvider>
                            <MindstonePlusUpsellProvider>
                              <ApiConnectivityNotice />
                              <GlobalPointsEarnedPopover />
                              <GlobalAIUsageReminderPopover />
                              <Outlet />
                            </MindstonePlusUpsellProvider>
                          </AIUsageProvider>
                        </FirstPointsProvider>
                      </TooltipProvider>
                    </ProfileInitializer>
                  </HubSpotInitializer>
                </AuthCheckProvider>
                {/* Live avatar coach — sibling to <Outlet/> inside
                    GrowthBookProvider + MainContext (Stage 5), kept out of
                    the HubSpot/Outlet subtree so the Toaster (below, inside
                    I18nProvider) is not disturbed. Re-mounted 2026-08-14
                    after the prod hotfix (6db7405092) unmounted it: the
                    launcher is no longer a `fixed bottom-5 right-5 z-40`
                    pill that overlaps bottom-anchored actions — it is a
                    vertical tab docked in the right-edge rail above "Chat to
                    us" (components/hubspot-initializer.tsx), so it cannot
                    cover a fixed bottom bar on any route, and it is the SOLE
                    coach entry point (Amendment 2.1) — on a day content card
                    it mints card mode via the mount's route-derived input.
                    The launcher is also gated on `LIVE_AVATAR_COACH_ENABLED`
                    (surfaced as `Config.liveAvatarCoachEnabled`) inside the
                    wrapper, so the env kill-switch hides the button instead
                    of leaving one that throws on click — the gap that made
                    the hotfix a code change rather than an env flip. */}
                <LiveAvatarCoachMount />
              </ApolloHydrationHelper>
            </ConfigContext.Provider>
          </MainContext.Provider>
        </GrowthBookProvider>
          {/* Toaster MUST stay inside <I18nProvider>: Sonner renders toast
              content (e.g. `<Trans>` action buttons) in this subtree, so a
              toast outside the provider reads a null Lingui context and, in a
              prod build (Lingui strips its dev guard), hard-crashes the page.
              See docs/plans/260616_fix-space-live-i18n-crash. */}
          <Toaster />
        </I18nProvider>
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export function ErrorBoundary() {
  const [theme, setTheme] = useState("light");

  useEffect(() => {
    const item = localStorage.getItem("ms-theme");
    setTheme(item || "light");
  }, []);

  return (
    <html lang="en" className={theme}>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        <Meta />
        <Links />
        <script
          type="text/javascript"
          id="hs-script-loader"
          async
          defer
          src="//js-eu1.hs-scripts.com/146597480.js"
        />
      </head>
      <body className="min-h-[100dvh] bg-background text-foreground">
        <I18nProvider i18n={i18n}>
          <GenericBoundary />
        </I18nProvider>
        <ScrollRestoration />
      </body>
    </html>
  );
}

export default App;

const Trackers = () => {
  return (
    <>
      <noscript>
        <img
          height="1"
          width="1"
          style={{ display: "none" }}
          src="https://www.facebook.com/tr?id=2676983045894747&ev=PageView&noscript=1"
        />
      </noscript>
      <script
        dangerouslySetInnerHTML={{
          __html: `
            (function Load3rdPartyScripts() {
              !function(f,b,e,v,n,t,s)
              {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
                      n.callMethod.apply(n,arguments):n.queue.push(arguments)};
                if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
                n.queue=[];t=b.createElement(e);t.async=!0;
                t.src=v;s=b.getElementsByTagName(e)[0];
                s.parentNode.insertBefore(t,s)}(window, document,'script',
                      'https://connect.facebook.net/en_US/fbevents.js');
              fbq('init', '2676983045894747');
              fbq('track', 'PageView');
            })()`,
        }}
      />
      <script
        type="text/javascript"
        dangerouslySetInnerHTML={{
          __html: `
          _linkedin_partner_id = "3583161";
          window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
          window._linkedin_data_partner_ids.push(_linkedin_partner_id);
       
          (function(l) {
          if (!l){window.lintrk = function(a,b){window.lintrk.q.push([a,b])};
          window.lintrk.q=[]}
          var s = document.getElementsByTagName("script")[0];
          var b = document.createElement("script");
          b.type = "text/javascript";b.async = true;
          b.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js";
          s.parentNode.insertBefore(b, s);})(window.lintrk);`,
        }}
      />
      <script
        type="text/javascript"
        dangerouslySetInnerHTML={{
          __html: `
          !function(q,e,v,n,t,s){if(q.qp) return; n=q.qp=function(){n.qp?n.qp.apply(n,arguments):n.queue.push(arguments);}; n.queue=[];t=document.createElement(e);t.async=!0;t.src=v; s=document.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t,s);}(window, 'script', 'https://a.quora.com/qevents.js');
          qp('init', 'd8eb09b79e1e4535a6dcf54d3c33e59d');
          qp('track', 'ViewContent');`,
        }}
      />
      <noscript>
        <img
          height="1"
          width="1"
          style={{ display: "none" }}
          src="https://q.quora.com/_/ad/d8eb09b79e1e4535a6dcf54d3c33e59d/pixel?tag=ViewContent&noscript=1"
        />
      </noscript>
      <script
        type="text/javascript"
        dangerouslySetInnerHTML={{
          __html: `
                !function(w,d){if(!w.rdt){var p=w.rdt=function(){p.sendEvent?p.sendEvent.apply(p,arguments):p.callQueue.push(arguments)};p.callQueue=[];var t=d.createElement("script");t.src="https://www.redditstatic.com/ads/pixel.js",t.async=!0;var s=d.getElementsByTagName("script")[0];s.parentNode.insertBefore(t,s)}}(window,document);`,
        }}
      />
      <script
        type="text/javascript"
        dangerouslySetInnerHTML={{
          __html: `!function(e,t,n,s,u,a){e.twq||(s=e.twq=function(){s.exe?s.exe.apply(s,arguments):s.queue.push(arguments);
          },s.version='1.1',s.queue=[],u=t.createElement(n),u.async=!0,u.src='https://static.ads-twitter.com/uwt.js',
          a=t.getElementsByTagName(n)[0],a.parentNode.insertBefore(u,a))}(window,document,'script');
          twq('config','obo6d');`,
        }}
      />
    </>
  );
};

const OurScripts = ({ webConfig }: { webConfig: WebConfig }) => {
  return (
    <>
      <script src="https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js" />
      <script src="https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js" />
      <script
        type="text/javascript"
        src="https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js"
      />
      <script
        dangerouslySetInnerHTML={{
          __html: `window.webConfig = ${JSON.stringify(webConfig)}`,
        }}
      />
      <script
        dangerouslySetInnerHTML={{
          __html: `window.installPrompt = null`,
        }}
      />
    </>
  );
};
